import { type VariablesOf } from '@graphql-typed-document-node/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { type AsyncReturnType } from 'type-fest'

import { graphql } from '~/gql'
import { useGetFigureMeta } from '~/hooks/useGetFigureMeta'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { addProp, FigureInfoHelpers } from '~/utils'

const document = graphql(`
  query DocumentImageInfiniteScrollList_DocumentImages(
    $brandAccountId: ID!
    $offset: Int! = 0
    $showAll: Boolean
    $searchWord: String
  ) {
    documentImages(
      brandAccountId: $brandAccountId
      status: active
      pagination: { limit: 20, offset: $offset }
      filter: { showAll: $showAll, searchWord: $searchWord }
    ) {
      id
      comment
      title
      imageURL
      pathKey
      restoreKey
      allMetadata {
        name
        value
      }
    }
  }
`)
type Variables = VariablesOf<typeof document>

export const useFetchDocumentImages = () => {
  const graphQLRequest = useGraphQLRequest()
  const getFigureMeta = useGetFigureMeta()
  const { t } = useTranslation()

  return React.useCallback(
    async (variables: Variables) => {
      const result = await graphQLRequest({ document, variables })

      return R.pipe(
        result.documentImages ?? [],
        R.map(
          R.piped(
            addProp('figureInfo', (documentImage) =>
              FigureInfoHelpers.fromDocumentImage(
                documentImage,
                variables.brandAccountId,
                t,
              ),
            ),
            addProp('figureMeta', getFigureMeta),
          ),
        ),
      )
    },
    [getFigureMeta, graphQLRequest, t],
  )
}

export type DocumentImage = AsyncReturnType<
  ReturnType<typeof useFetchDocumentImages>
>[number]
